import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Button } from "../components/StyledComponents";
import { captureSurveyOrigem, markSurveyStarted } from "../utils/surveySession";

const HomePageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
    align-items: center;
    padding: 16px;
  }
`;

export const BannerBackground = styled.div`
  background: url("/gemini.png") center no-repeat;
  background-size: cover;
  width: 100%;
  height: 100vh;
  position: fixed;
  z-index: 1;
`;

const WelcomeText = styled.div`
  margin-bottom: auto;

  h1 {
    font-size: 2rem;
    line-height: 1.2;
    margin-bottom: 1rem;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 1.6rem;
    }
  }
`;

const WelcomeParagraph = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  line-height: 1.8;

  p {
    margin: 0.75rem 0 0;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.65;
  }
`;

const PrivacyConsent = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 20px 0 16px;
  font-size: 0.95rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  user-select: none;

  input {
    margin-top: 3px;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    accent-color: #9e2480;
    cursor: pointer;
  }

  a {
    color: #fff;
    text-decoration: underline;
    font-weight: 600;

    &:hover {
      opacity: 0.9;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.88rem;
    margin: 16px 0 14px;
  }
`;

const CTAButton = styled(Button)`
  background: #9e2480;
  font-size: 18px;
  padding: 16px 32px;
  border-radius: 50px;
  box-shadow: 0 8px 25px rgba(158, 36, 128, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.25);
    color: rgba(255, 255, 255, 0.7);
    box-shadow: none;
    cursor: not-allowed;
    transform: none;

    &::before {
      display: none;
    }
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 14px 28px;
  }
`;

const RightColumn = styled.div`
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 543px;
  min-height: 100vh;
  padding: 70px;
  background: linear-gradient(214deg, #084d6e 0%, #9e2480 100%);
  color: #fff;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 360px;
    min-height: auto;
    padding: 28px 24px 32px;
    border-radius: 24px;
    margin: 0 auto;
  }
`;

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  useEffect(() => {
    captureSurveyOrigem(location.search || window.location.search);
  }, [location.search]);

  const handleStartSurvey = () => {
    if (!acceptedPrivacy) return;
    markSurveyStarted(location.search || window.location.search, {
      privacyAccepted: true,
    });
    navigate({
      pathname: "/survey",
      search: location.search || window.location.search,
    });
  };

  return (
    <HomePageContainer>
      <BannerBackground />
      <RightColumn>
        <WelcomeText>
          <h1>Força ou Bloqueio</h1>
          <WelcomeParagraph>
            <p>
              Descubra o grau de maturidade de sua gestão de pessoas e saiba se
              isso é uma alavanca ou trava para o seu negócio.
            </p>
            <p>Entenda:</p>
            <p>- O clima organizacional e liderança</p>
            <p>
              - As métricas modernas e essenciais para a gestão de pessoas como
              ISP, e-NPS, TTH, Turnover, etc
            </p>
            <p>
              - A cultura, o nivel de segurança psicológica e o ambiente
              corporativo.
            </p>
            <p>
              Os dados coletados são exclusivos para este diagnostico e não serão
              compartilhados, nem distribuidos para terceiros
            </p>
          </WelcomeParagraph>
        </WelcomeText>

        <PrivacyConsent>
          <input
            type="checkbox"
            checked={acceptedPrivacy}
            onChange={(e) => setAcceptedPrivacy(e.target.checked)}
            aria-label="Li e concordo com a Política de Privacidade"
          />
          <span>
            Li e concordo com a{" "}
            <a
              href="/politica-de-privacidade.pdf"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Política de Privacidade
            </a>
          </span>
        </PrivacyConsent>

        <CTAButton
          variant="primary"
          onClick={handleStartSurvey}
          disabled={!acceptedPrivacy}
        >
          Vamos começar
        </CTAButton>
      </RightColumn>
    </HomePageContainer>
  );
};

export default HomePage;
